import { useMutation } from "@tanstack/react-query";
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { resendInviteForTeamMember } from "api/landlord/landlord";
import { FC, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Table } from "shared/app/Table";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { Building06, Lock02, Mail01, Plus, Trash02 } from "untitledui-js-base";
import { getErrorMessage } from "utils/error";
import { RoleTitles } from "../constants";
import { ROLE, useAuthContext } from "providers/Auth";
import { User } from "../types";
import { Button, ButtonVariant } from "shared/components";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { getShortAddressFromAddress } from "shared/addressUtils";

type Props = {
  users: User[];
  onRemoveUserClick: (user: any) => void;
};

export const UsersTable: FC<Props> = ({ users, onRemoveUserClick }) => {
  const { auth } = useAuthContext();
  const navigate = useNavigate();
  const { mutate: resendInvite } = useMutation({
    mutationFn: (userId: number) => {
      return resendInviteForTeamMember({ userId });
    },
    onSuccess: () => {
      toast.success("Invite sent");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
    mutationKey: ["RESEND_INVITE"]
  });
  const columns = useMemo(() => {
    return [
      {
        id: "user",
        header: "User",
        size: 430,
        cell: ({ row }) => {
          return (
            <div>
              <p className="text-sm font-medium text-content-1000 mb-1">
                {row.original.name}{" "}
                {row.original.id === auth?.id ? (
                  <span className="text-content-100 font-medium text-sm">
                    (you)
                  </span>
                ) : null}
              </p>

              <p className="text-sm  text-content-400">{row.original.email}</p>
            </div>
          );
        }
      },
      {
        accessorKey: "properties",
        header: "Property access",
        size: 430,
        cell: (p) => <PropertiesCell user={p.row.original} />
      },
      {
        accessorKey: "role",
        header: "User role",
        size: 228,
        cell: (p) => {
          return (
            <p className="text-sm text-content-1000 font medium">
              {RoleTitles[p.getValue()] || "--"}
            </p>
          );
        }
      },
      {
        id: "popover-action",
        header: "",
        size: 60,
        cell: (p) => {
          const isOwner = p.row?.original?.role === ROLE.Owner;
          const actions = [
            {
              title: (
                <span className="flex gap-3 items-center">
                  <Lock02 size="20" className="text-content-200" />
                  View permissions
                </span>
              ),
              onClick: () => {
                navigate(`/settings/users/${p.row.original.id}`);
              }
            },
            {
              title: (
                <span className="flex gap-3 items-center">
                  <Mail01 size="20" className="text-content-200" />
                  Resend invite
                </span>
              ),
              onClick: () => resendInvite(p.row.original.id)
            },
            {
              title: (
                <span className="flex gap-3 items-center">
                  <Trash02 size="20" className="text-content-200" />
                  Remove user
                </span>
              ),
              disabled: isOwner,
              onClick: () => !isOwner && onRemoveUserClick(p.row.original),
              tooltip: "Can't remove an owner"
            }
          ];

          return <ContextMenuPopover actions={actions} />;
        }
      }
    ];
  }, [auth]);
  const table = useReactTable({
    data: users,
    columns: columns as ColumnDef<any, any>[],
    getCoreRowModel: getCoreRowModel()
  });
  return (
    <Table
      table={table}
      onRowClick={(id) => {
        navigate(`/settings/users/${id}`);
      }}
    />
  );
};

const PropertiesCell = ({ user }: { user: User }) => {
  const propertiesCount = user.properties.length;
  const [isHovered, setIsHovered] = useState(false);
  const popoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (popoverTimeoutRef.current) clearTimeout(popoverTimeoutRef.current);

    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    popoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 200);
  };

  return (
    <Popover open={isHovered} onOpenChange={setIsHovered}>
      <PopoverTrigger
        asChild
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex gap-2 items-center">
          <Building06 />
          <p className="text-sm text-content-500">
            {[ROLE.Owner, ROLE.Admin].includes(user.role) ? (
              "All properties"
            ) : (
              <>
                {propertiesCount}{" "}
                {propertiesCount === 1 ? "property" : "properties"}
              </>
            )}
          </p>
        </div>
      </PopoverTrigger>
      {[ROLE.Owner, ROLE.Admin].includes(user.role) ? null : (
        <PopoverContent
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="absolute left-full top-0 bg-background-1000  py-3 shadow-xl rounded-lg border border-alpha-100 w-[370px] overflow-y-auto max-h-[640px]">
            <Link to={`/settings/users/${user.id}`}>
              <Button
                variant={ButtonVariant.TEXT}
                Icon={Plus}
                className="flex items-center gap-3 px-4"
              >
                Add property
              </Button>
            </Link>
            {propertiesCount > 0 ? (
              <>
                <div className="border-b border-alpha-40 mt-3" />
                {user.properties.map((property) => {
                  return (
                    <div
                      key={property.id}
                      onClick={(e) => e.stopPropagation()}
                      className="border-b border-alpha-40 py-3 last-of-type:border-b-0 last-of-type:pb-0"
                    >
                      <p className="text-sm text-content-1000 font-medium px-4">
                        {property.name ||
                          getShortAddressFromAddress(property.address) ||
                          ""}
                      </p>
                    </div>
                  );
                })}
              </>
            ) : null}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};
